import { Check, Circle } from "lucide-react";
import { cn } from "@/lib/utils";

interface SetupProgressProps {
  managerSelected: boolean;
  reviewsConfirmed: boolean;
  csiCodesVerified: boolean;
  isProcoreTemplate?: boolean;
  className?: string;
}

interface Step {
  id: string;
  label: string;
  complete: boolean;
}

function getSetupSteps({
  managerSelected,
  reviewsConfirmed,
  csiCodesVerified,
  isProcoreTemplate = true,
}: Omit<SetupProgressProps, "className">): Step[] {
  return isProcoreTemplate
    ? [
        { id: "manager", label: "Manager assigned", complete: managerSelected },
        { id: "review", label: "Review times set", complete: reviewsConfirmed },
        { id: "csi", label: "CSI codes verified", complete: csiCodesVerified },
      ]
    : [
        {
          id: "verify",
          label: "Verify all information is correct",
          complete: managerSelected && reviewsConfirmed && csiCodesVerified,
        },
      ];
}

/** Inline setup checklist ("2/3 · ✓ Manager assigned · ○ Review times set …"). */
export function SetupProgress({ className, ...stepArgs }: SetupProgressProps) {
  const steps = getSetupSteps(stepArgs);
  const completedCount = steps.filter((s) => s.complete).length;

  return (
    <div className={cn("flex min-w-0 items-center gap-3 text-xs", className)}>
      <span className="shrink-0 tabular-nums text-muted-foreground">
        <span className="font-semibold text-foreground">{completedCount}/{steps.length}</span> complete
      </span>
      <ul className="hidden min-w-0 items-center gap-3 md:flex">
        {steps.map((step) => (
          <li key={step.id} className="flex shrink-0 items-center gap-1">
            {step.complete ? (
              <Check className="size-3.5 text-primary" aria-hidden="true" />
            ) : (
              <Circle className="size-3.5 text-muted-foreground/50" aria-hidden="true" />
            )}
            <span className={step.complete ? "font-medium text-foreground" : "text-muted-foreground"}>
              {step.label}
            </span>
            <span className="sr-only">{step.complete ? "(done)" : "(to do)"}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
